// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { Resources } from '@shared/localizations/Resources';
import { Point, Rectangle } from '@shared/commons/Geometry';
import { Cursors } from '@shared/commons/Cursors';
import { TerceraChartCashItemSeriesDataType } from '../../Series/TerceraChartCashItemSeriesEnums';
import { Line, Pen, PenStyle, PolyLine, PolyText, PolyTextItem, SolidBrush } from '@shared/commons/Graphics';
import { MathUtils } from '@shared/utils/MathUtils';
import { MouseButtons } from '@front/controls/UtilsClasses/ControlsUtils';
import { TerceraChartAction, TerceraChartActionEnum } from '../../TerceraChartAction';
import { TerceraChartNumberScaleRenderer, TerceraChartNumberScaleRendererSettings } from './TerceraChartNumberScaleRenderer';
import { ThemeManager } from '@front/controls/misc/ThemeManager';
import { DynProperty, BoolNumeric, ColorStyleWidth } from '@shared/commons/DynProperty';
import { PriceFormatter } from '@shared/utils/Instruments/PriceFormatter';
import { DataCache } from '@shared/commons/DataCache';
import { NewContextMenuItemType } from '@shared/utils/ContextMenuUtils';
export class TerceraChartPriceScaleRenderer extends TerceraChartNumberScaleRenderer {
    constructor(terceraChart, settings, windowType = TerceraChartPriceScaleRendererWindowType.Main) {
        super(settings, terceraChart);
        this.maxC = null;
        this.minC = null;
        this.mouseState = TerceraChartPriceScaleRendererMouseState.None;
        this.lastMouseDownPt = null;
        this.lastMouseDownMaxY = null;
        this.lastMouseDownMinY = null;
        this.autoText = null;
        this.manualText = null;
        this.collapsedText = null;
        this.Aligment = TerceraChartPriceScaleRendererAligment.Right;
        this.DigitsBasedOnIndicator = -1;
        this.resetMouseDown = null;
        this.yDeltaPx = null;
        this.yDeltaPrice = null;
        this.priceScaleRenderer = true;
        this.WindowType = windowType;
        this.Instrument = terceraChart.Instrument();
        this.Rectangle = new Rectangle();
        this.AutoScaleRect = new Rectangle();
        this.AutoScaleSwitcherRectangle = new Rectangle();
        this.ScaleButtonRect = new Rectangle();
        this.Localize();
        this.SetClassName('TerceraChartPriceScaleRenderer');
    }
    get Instrument() {
        return this._instrument;
    }
    set Instrument(value) {
        this._instrument = value;
    }
    Draw(gr, window, windowsContainer, advParams = null) {
        var _a;
        if (!this.Visible)
            return;
        const R = this.Rectangle;
        const RX = R.X;
        const RY = R.Y;
        const RW = R.Width;
        const RH = R.Height;
        const RB = RY + RH;
        const RBH = windowsContainer.Rectangle.Height;
        const advParamsObj = advParams;
        const cashItemSeriesSettings = (_a = advParamsObj.mainPriceRenderer.Series) === null || _a === void 0 ? void 0 : _a.settings;
        gr.fillStyle = this.settings.scaleBackBrush.Color;
        gr.fillRect(RX, RY, RW, RBH);
        const minV = window.FminFloatY;
        const maxV = window.FmaxFloatY;
        if (minV === Number.MAX_VALUE || maxV === -Number.MAX_VALUE || minV === maxV)
            return;
        if (window.Collapsed) {
            return;
        }
        const windowClientRect = window.ClientRectangle;
        gr.save();
        gr.beginPath();
        if (this.Aligment === TerceraChartPriceScaleRendererAligment.Right) {
            gr.rect(windowClientRect.X, windowClientRect.Y, windowClientRect.Width + RW, windowClientRect.Height);
        }
        else {
            gr.rect(0, windowClientRect.Y, windowClientRect.Width + RW, windowClientRect.Height);
        }
        gr.clip();
        const FontHeightPixels = this.GetScaleFontHeight();
        this.maxC = RB;
        this.minC = RY;
        let step = this.calcStep(maxV, minV, this.maxC, this.minC, FontHeightPixels + 40);
        const instrument = advParamsObj.Instrument;
        const activeInstrumentPipSize = instrument != null ? instrument.MinimumPipsSize(true) : 0;
        const activeInstrumentPrecision = instrument != null ? instrument.Precision : 0;
        const activeInstrumentPrecisionMultiplier = Math.pow(10, activeInstrumentPrecision);
        let seriesDataType = TerceraChartCashItemSeriesDataType.Absolute;
        switch (this.WindowType) {
            case TerceraChartPriceScaleRendererWindowType.Main:
            case TerceraChartPriceScaleRendererWindowType.Overlay:
                seriesDataType = !isNullOrUndefined(cashItemSeriesSettings)
                    ? cashItemSeriesSettings.DataType
                    : TerceraChartCashItemSeriesDataType.Absolute;
                break;
            // For indicator - always use absolute
            case TerceraChartPriceScaleRendererWindowType.Indicator:
                seriesDataType = TerceraChartCashItemSeriesDataType.Absolute;
                break;
        }
        step = this.CorrectStep(step, instrument, activeInstrumentPipSize, seriesDataType);
        const start = this.calcStart(step, minV);
        let iterCount = 0;
        const gridPolyLines = new PolyLine();
        const highlightMarkingsPolyLines = new PolyLine();
        const gridPolyText = new PolyText();
        const highlightMarkingsStep = this.settings.HighlightMarkingsStep;
        const showHighlightMarkings = this.settings.HighlightMarkings;
        const pointsConverter = window.PointsConverter;
        const finish = maxV + 3 * step;
        let pipSizeWithStep = activeInstrumentPipSize * highlightMarkingsStep;
        pipSizeWithStep = MathUtils.RoundDouble(pipSizeWithStep, activeInstrumentPrecision);
        const scaleFont = this.GetScaleFont();
        for (let vi = start; vi <= finish; vi += step) {
            // Protection against looping
            if (iterCount > 1000)
                return;
            iterCount++;
            const ty = this.maxC + Math.round(pointsConverter.GetScreenY(vi)) - RB;
            // Beyond visible y scale.
            const scaleBtnOffset = this.WindowType !== TerceraChartPriceScaleRendererWindowType.Indicator
                ? this.ScaleButtonOffset + scaleFont.Height / 2
                : 0;
            if (ty <= RY || ty >= RB - scaleBtnOffset) {
                continue;
            }
            if (this.Aligment === TerceraChartPriceScaleRendererAligment.Right &&
                this.settings.ScaleGridVisibility &&
                window.PaddingTop < ty) {
                // определяем кратность Highlight markings step
                let highlightMarkings = false;
                if (showHighlightMarkings && pipSizeWithStep !== 0) {
                    const newVI = Math.round(MathUtils.RoundDouble(vi, activeInstrumentPrecision) * activeInstrumentPrecisionMultiplier);
                    const newPips = Math.round(pipSizeWithStep * activeInstrumentPrecisionMultiplier);
                    highlightMarkings = newVI % newPips === 0;
                }
                if (highlightMarkings) {
                    highlightMarkingsPolyLines.lines.push(new Line(windowClientRect.X + 1, ty, RX, ty));
                }
                else {
                    gridPolyLines.lines.push(new Line(windowClientRect.X + 1, ty, RX, ty));
                }
            }
            let formattedValue = null;
            if (formattedValue === null) {
                formattedValue = TerceraChartPriceScaleRenderer.FormatPrice(vi, instrument, cashItemSeriesSettings, seriesDataType, this);
            }
            gridPolyText.textArray.push(new PolyTextItem(formattedValue, RX + TerceraChartPriceScaleRenderer.LEFT_PADDING, Math.floor(ty - FontHeightPixels / 2 + 1)));
        }
        gr.DrawPolyLine(this.settings.scaleGridPen, gridPolyLines);
        if (showHighlightMarkings) {
            gr.DrawPolyLine(this.settings.gridPriceHLPen, highlightMarkingsPolyLines);
        }
        gr.DrawPolyText(gridPolyText, scaleFont, this.settings.scaleTextBrush);
        if (this.chart.PortfolioAssetReturnVisible) {
            this.DrawPortfolioAssetReturnText(gr, windowClientRect);
        }
        // Reset clip.
        gr.restore();
        this.drawAutoSwitcher(gr, window.AutoScale, RX, RY, RW, RH);
        if (this.WindowType !== TerceraChartPriceScaleRendererWindowType.Indicator &&
            !(this.WindowType === TerceraChartPriceScaleRendererWindowType.Overlay &&
                DataCache.EntitlementManager.IsDataSourceAvailable(instrument))) {
            const SIZE = TerceraChartPriceScaleRenderer.SCALE_BUTTON_SIZE;
            const margin = 4;
            const offsetY = (this.ScaleButtonOffset =
                SIZE -
                    (windowsContainer.Windows.length > 1 ? 0 : windowsContainer.xScaleRenderer.GetPreferredHeight()) +
                    margin);
            const x = this.Rectangle.X + margin;
            const y = this.Rectangle.Y + this.Rectangle.Height - offsetY;
            const backgroundBrush = this.getButtonBackgroundBrush();
            const borderPen = this.getButtonBorderPen();
            const canvasImage = this.getButtonImage();
            this.ScaleButtonRect.X = x;
            this.ScaleButtonRect.Y = y;
            this.ScaleButtonRect.Width = SIZE;
            this.ScaleButtonRect.Height = SIZE;
            gr.RoundRectangle(this.ScaleButtonRect, 4, backgroundBrush, borderPen);
            gr.DrawCenteredImage(canvasImage.image, this.ScaleButtonRect);
        }
        // Reset clip.
        gr.restore();
        // Draw shadow
        if ((this.WindowType === TerceraChartPriceScaleRendererWindowType.Main && this.chart.FocusedOverlay != null) ||
            (this.WindowType === TerceraChartPriceScaleRendererWindowType.Overlay && this.chart.FocusedOverlay == null)) {
            gr.FillRect(this.settings.scaleBackBrushShadow, RX, RY, RW, RBH);
        }
    }
    GetPreferredWidth(gr) {
        const instrument = this.Instrument;
        const cashItemSeriesSettings = this.getCashItemSeriesSettings();
        const values = [this.window.FminFloatY, this.window.FmaxFloatY];
        let seriesDataType = TerceraChartCashItemSeriesDataType.Absolute;
        switch (this.WindowType) {
            case TerceraChartPriceScaleRendererWindowType.Main:
            case TerceraChartPriceScaleRendererWindowType.Overlay:
                seriesDataType = !isNullOrUndefined(cashItemSeriesSettings)
                    ? cashItemSeriesSettings.DataType
                    : TerceraChartCashItemSeriesDataType.Absolute;
                break;
            // For indicator - always use absolute
            case TerceraChartPriceScaleRendererWindowType.Indicator:
                seriesDataType = TerceraChartCashItemSeriesDataType.Absolute;
                break;
        }
        let maxW = 0;
        for (let i = 0; i < values.length; i++) {
            let formattedValue = '';
            if (!isNullOrUndefined(instrument)) {
                if (!isNullOrUndefined(cashItemSeriesSettings)) {
                    formattedValue = cashItemSeriesSettings.FormatPriceConverter(values[i], instrument, seriesDataType);
                }
                else {
                    formattedValue = instrument.formatPrice(values[i], false);
                }
            }
            else {
                formattedValue = values[i].toString();
            }
            gr.font = this.GetScaleFont().toString();
            const curW = gr.measureText(formattedValue).width;
            if (maxW < curW) {
                maxW = curW;
            }
        }
        if (this.settings.AutoScaleSwitcherVisible) {
            const manualW = gr.measureText('Manual').width;
            if (maxW < manualW) {
                maxW = manualW;
            }
        }
        return maxW + 5;
    }
    static FormatPrice(vi, instrument, seriesSettings, seriesDataType, scaleRenderer) {
        let formattedValue = '';
        if (scaleRenderer.WindowType === TerceraChartPriceScaleRendererWindowType.Indicator) {
            if (scaleRenderer.DigitsBasedOnIndicator !== -1) {
                formattedValue = PriceFormatter.formatPrice(vi, scaleRenderer.DigitsBasedOnIndicator);
            }
            else if (!isNullOrUndefined(instrument)) {
                formattedValue = instrument.formatPrice(vi, false);
            }
        }
        else if (!isNullOrUndefined(instrument)) {
            if (!isNullOrUndefined(seriesSettings)) {
                formattedValue = seriesSettings.FormatPriceScaleConverter(vi, instrument, seriesDataType);
            }
            else {
                formattedValue = instrument.formatPrice(vi, false);
            }
        }
        else {
            formattedValue = vi.toString();
        }
        return formattedValue;
    }
    CorrectStep(step, instrument, activeInstrumentPipSize, seriesDataType) {
        if (instrument != null && seriesDataType === TerceraChartCashItemSeriesDataType.Absolute) {
            // 1. Шаг не меньше, чем MinDelta цены инструмента
            const MinDelta = instrument.MinDelta;
            if (step < MinDelta)
                step = MinDelta;
            // 2. Для форексных инструментов, не меньше чем PipSize
            if (instrument.isForexSymbol() === true) {
                if (activeInstrumentPipSize != 0 && step < activeInstrumentPipSize)
                    step = activeInstrumentPipSize;
                // +++ round to pip size
                if (activeInstrumentPipSize != 0)
                    step = MathUtils.RoundTo(step, activeInstrumentPipSize);
            }
        }
        // +++ Custom Y scale
        if (this.settings.UseCustomYMarkings === true) {
            const customStep = this.settings.CustomYMarkingValue * activeInstrumentPipSize;
            if (step / customStep >= 2) {
                // слишком часто
            }
            else {
                step = customStep;
            }
        }
        return step;
    }
    GetContextMenu(e, chart) {
        const window = this.window;
        const location = e.Location;
        if (!this.Visible || window.Collapsed === true || !this.Rectangle.Contains(location.X, location.Y)) {
            return [];
        }
        const actionProcessor = chart.TerceraChartActionProcessor;
        const menuItems = [
            actionProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.AutoScale, window)),
            actionProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.ManualScale, window)),
            { type: NewContextMenuItemType.Separator }
        ];
        const WindowType = this.WindowType;
        if (WindowType !== TerceraChartPriceScaleRendererWindowType.Overlay) {
            menuItems.push(actionProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.FitIndicators, window)));
        }
        if (WindowType === TerceraChartPriceScaleRendererWindowType.Main) {
            menuItems.push(actionProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.FitOrders)));
            menuItems.push(actionProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.FitDayHighLow)));
        }
        menuItems.push(actionProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.FitDrawings, window)));
        if (WindowType === TerceraChartPriceScaleRendererWindowType.Main) {
            menuItems.push(actionProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.FitAlerts)));
        }
        if (WindowType === TerceraChartPriceScaleRendererWindowType.Main ||
            WindowType === TerceraChartPriceScaleRendererWindowType.Overlay) {
            const overlays = this.chart.Overlays;
            const len = overlays.length;
            if (this.Aligment === TerceraChartPriceScaleRendererAligment.Left && len > 1) {
                menuItems.push({ type: NewContextMenuItemType.Separator });
                for (let i = 0; i < len; i++) {
                    menuItems.push(actionProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.MainSymbol, overlays[i])));
                }
            }
        }
        // #region Scale type
        if (WindowType === TerceraChartPriceScaleRendererWindowType.Main ||
            WindowType === TerceraChartPriceScaleRendererWindowType.Overlay) {
            menuItems.push({ type: NewContextMenuItemType.Separator });
            menuItems.push(actionProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.Absolute, window)));
            menuItems.push(actionProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.Relative, window)));
            menuItems.push(actionProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.Log, window)));
        }
        // #endregion
        return menuItems;
    }
    Localize() {
        super.Localize();
        this.autoText = Resources.getResource('Chart.chartWindow.auto');
        this.manualText = Resources.getResource('Chart.chartWindow.manual');
        this.collapsedText = Resources.getResource('Chart.chartWindow.collapsed');
    }
    DrawPortfolioAssetReturnText(gr, windowClientRect) {
        if (this.chart.mainWindow.CanZoomIn()) {
            return;
        }
        if (this.assetReturnText == null) {
            this.assetReturnText = Resources.getResource('portfolioReturns.AssetReturnText');
        }
        if (this.assetReturnBrush == null) {
            this.assetReturnBrush = new SolidBrush(ThemeManager.colors.PortfolioReturnsChartAssetReturnTextBackground);
        }
        const w = windowClientRect.Width;
        const h = windowClientRect.Height;
        const text = this.assetReturnText;
        const margin = 6;
        const rectHeight = 14;
        const textW = gr.GetTextWidth(text, this.GetScaleFont()) + margin * 2;
        gr.FillRect(this.assetReturnBrush, w, h - rectHeight, textW, rectHeight - 1);
        gr.DrawRect(this.settings.scaleAxisPen, w, h - rectHeight, textW, rectHeight - 1);
        gr.DrawString(text, this.GetScaleFont(), this.settings.scaleTextBrush, windowClientRect.Width + margin, windowClientRect.Height - margin * 2); //, 'center', 'middle');
    }
    // #region Process mouse events
    /// <summary>
    ///
    /// </summary>
    ProcessMouseDown(e) {
        var _a, _b, _c, _d;
        // reset overlay
        if (this.WindowType === TerceraChartPriceScaleRendererWindowType.Main &&
            this.Rectangle.Contains(e.Location.X, e.Location.Y)) {
            this.chart.FocusedOverlay = null;
        }
        // activate overlay
        else if (this.Visible &&
            this.WindowType === TerceraChartPriceScaleRendererWindowType.Overlay &&
            this.Rectangle.Contains(e.Location.X, e.Location.Y)) {
            this.chart.FocusedOverlay = this.window.Overlay;
        }
        if (!this.Visible || e.Button !== MouseButtons.Left || this.window.Collapsed === true) {
            return false;
        }
        this.lastMouseDownPt = e.Location;
        this.mouseState = TerceraChartPriceScaleRendererMouseState.None;
        if (this.AutoScaleRect.Contains(e.Location.X, e.Location.Y) &&
            e.window != null &&
            e.window.Collapsed === false) {
            const newValue = this.window.AutoScale !== true;
            if (this.WindowType === TerceraChartPriceScaleRendererWindowType.Overlay) {
                for (let i = 0; i < this.chart.Overlays.length; i++) {
                    this.chart.Overlays[i].window.AutoScale = newValue;
                }
            }
            else {
                this.window.AutoScale = newValue;
            }
            return true;
        }
        else if (this.ScaleButtonRect.Contains(e.Location.X, e.Location.Y)) {
            (_b = (_a = e._baseEvent).preventDefault) === null || _b === void 0 ? void 0 : _b.call(_a);
            (_d = (_c = e._baseEvent).stopPropagation) === null || _d === void 0 ? void 0 : _d.call(_c);
            return true;
        }
        else if (this.Rectangle.Contains(e.Location.X, e.Location.Y)) {
            this.lastMouseDownMinY = this.window.FminFloatY;
            this.lastMouseDownMaxY = this.window.FmaxFloatY;
            this.mouseState = TerceraChartPriceScaleRendererMouseState.Moving;
            if (this.WindowType === TerceraChartPriceScaleRendererWindowType.Overlay) {
                this.chart.FocusedOverlay = this.window.Overlay;
            }
            else if (this.WindowType === TerceraChartPriceScaleRendererWindowType.Main) {
                this.chart.FocusedOverlay = null;
            }
            return true;
        }
        return false;
    }
    ProcessMouseClick(e) {
        if (this.ScaleButtonRect.Contains(e.Location.X, e.Location.Y)) {
            return this.scaleButtonMouseClick(e);
        }
        return false;
    }
    /// <summary>
    ///
    /// </summary>
    ProcessMouseMove(e) {
        var _a;
        if (!this.Visible) {
            return false;
        }
        if (this.resetMouseDown) {
            // если выводим за шкалу мышку, то в момент ввода ее назад будем считать, что точка ввода - начальная
            // чтобы не было резких скачков относительно старого значения lastMouseDownPt
            this.lastMouseDownPt = e.Location;
            this.lastMouseDownMinY = this.window.FminFloatY;
            this.lastMouseDownMaxY = this.window.FmaxFloatY;
            this.resetMouseDown = false;
        }
        const { X: RX, Y: RY, Height: RH, Width: RW } = this.Rectangle;
        if (this.mouseState === TerceraChartPriceScaleRendererMouseState.Moving &&
            e.Location.X > RX &&
            e.Location.X < RX + RW /* по ширине */ &&
            e.Location.Y > RY &&
            e.Location.Y < RY + RH) /* по высоте */ {
            this.yDeltaPx = (e.Location.Y - ((_a = this.lastMouseDownPt) === null || _a === void 0 ? void 0 : _a.Y)) / RH; // коефициент в процентах относительно пикселей
            if (Math.abs(this.yDeltaPx) > 0.01) {
                // начинаем двигать без маленьких движений в начале
                this.yDeltaPrice = (this.yDeltaPx * (this.window.FmaxFloatY - this.window.FminFloatY)) / 2; // коефициент в процентах относительно цены
                this.window.AutoScale = false;
                // check
                if (this.lastMouseDownMinY && this.lastMouseDownMaxY) {
                    this.window.MoveY(this.lastMouseDownMinY - this.yDeltaPrice, this.lastMouseDownMaxY + this.yDeltaPrice);
                }
                e.NeedRedraw = true;
            }
        }
        else {
            this.resetMouseDown = true;
        }
        if (this.AutoScaleRect.Contains(e.Location.X, e.Location.Y)) {
            e.NeedRedraw = true;
        }
        const newSwitcherState = this.AutoScaleSwitcherRectangle.Contains(e.Location.X, e.Location.Y);
        if (newSwitcherState !== this.AutoScaleSwitcherHovered) {
            e.NeedRedraw = true;
        }
        this.AutoScaleSwitcherHovered = newSwitcherState;
        this.hoverScaleButton(e);
        return super.ProcessMouseMove(e); // return TerceraChartNumberScaleRenderer.prototype.ProcessMouseMove.call(this, e);
    }
    ProcessMouseUp(e) {
        super.ProcessMouseUp(e);
        this.resetMouseDown = false;
        this.mouseState = TerceraChartPriceScaleRendererMouseState.None;
        return false;
    }
    ProcessMouseEnter(e) {
        super.ProcessMouseEnter(e);
        if (this.mouseState === TerceraChartPriceScaleRendererMouseState.Moving) {
            this.resetMouseDown = true;
        }
    }
    ProcessMouseDoubleClick(e) {
        if (this.window.Collapsed === false && this.Rectangle.Contains(e.Location.X, e.Location.Y)) {
            this.window.AutoFit();
            return true;
        }
        else {
            return false;
        }
    }
    ProcessMouseLeave(e) {
        super.ProcessMouseLeave(e);
        this.isHoverScaleButton = false;
    }
    // #endregion
    /// /#region Misc
    AutoScale_ON(e) {
        this.chart.TerceraChartActionProcessor.ProcessTerceraChartAction(TerceraChartAction.Create(TerceraChartActionEnum.AutoScale, this.window));
    }
    Dispose() {
        this.window = null;
        super.Dispose();
    }
    GetCursor(e) {
        if (this.window.Collapsed === true) {
            return super.GetCursor(e);
        }
        if (this.mouseState === TerceraChartPriceScaleRendererMouseState.Moving &&
            this.Rectangle.Contains(e.Location.X, e.Location.Y)) {
            return Cursors.SizeNS;
        }
        if (this.AutoScaleSwitcherRectangle.Contains(e.Location.X, e.Location.Y)) {
            return Cursors.Hand;
        }
        if (this.ScaleButtonRect.Contains(e.Location.X, e.Location.Y)) {
            return Cursors.Hand;
        }
        return null;
    }
    GetTooltip(e) {
        if (!this.ScaleButtonRect.Contains(e.Location.X, e.Location.Y)) {
            return null;
        }
        const cashItemSeriesSettings = this.getCashItemSeriesSettings();
        if (isNullOrUndefined(cashItemSeriesSettings)) {
            return null;
        }
        let state;
        switch (cashItemSeriesSettings.DataType) {
            case TerceraChartCashItemSeriesDataType.Absolute:
                state = this.chart.TerceraChartActionProcessor.GetTerceraChartActionState(TerceraChartAction.Create(TerceraChartActionEnum.Absolute));
                break;
            case TerceraChartCashItemSeriesDataType.Relative:
                state = this.chart.TerceraChartActionProcessor.GetTerceraChartActionState(TerceraChartAction.Create(TerceraChartActionEnum.Relative));
                break;
            case TerceraChartCashItemSeriesDataType.Log:
                state = this.chart.TerceraChartActionProcessor.GetTerceraChartActionState(TerceraChartAction.Create(TerceraChartActionEnum.Log));
                break;
        }
        if ((state === null || state === void 0 ? void 0 : state.ToolTiplocKey) !== null) {
            return Resources.getResource(state.ToolTiplocKey);
        }
        return null;
    }
    // #region Scale button
    getButtonBackgroundBrush() {
        if (this.isScaleMenuOpen) {
            if (this.isHoverScaleButton) {
                return new SolidBrush(ThemeManager.colors.chartScaleSelectorBackgroundSelectedHovered);
            }
            else {
                return new SolidBrush(ThemeManager.colors.chartScaleSelectorBackgroundSelected);
            }
        }
        else if (this.isHoverScaleButton) {
            return new SolidBrush(ThemeManager.colors.chartScaleSelectorBackgroundHovered);
        }
        return new SolidBrush(ThemeManager.colors.chartScaleSelectorBackground);
    }
    getButtonBorderPen() {
        if (this.isScaleMenuOpen) {
            if (this.isHoverScaleButton) {
                return new Pen(ThemeManager.colors.chartScaleSelectorBorderSelected);
            }
            else {
                return new Pen(ThemeManager.colors.chartScaleSelectorBorderSelectedHovered);
            }
        }
        else if (this.isHoverScaleButton) {
            return new Pen(ThemeManager.colors.chartScaleSelectorBorderHovered);
        }
        return new Pen(ThemeManager.colors.chartScaleSelectorBorder);
    }
    getButtonImage() {
        const images = ThemeManager.NewImages;
        const cashItemSeriesSettings = this.getCashItemSeriesSettings();
        switch (cashItemSeriesSettings.DataType) {
            case TerceraChartCashItemSeriesDataType.Absolute:
                if (this.isScaleMenuOpen) {
                    return images.scaleSelectorAbsoluteSelected;
                }
                else if (this.isHoverScaleButton) {
                    return images.scaleSelectorAbsoluteHovered;
                }
                else {
                    return images.scaleSelectorAbsolute;
                }
            case TerceraChartCashItemSeriesDataType.Relative:
                if (this.isScaleMenuOpen) {
                    return images.scaleSelectorRelativeSelected;
                }
                else if (this.isHoverScaleButton) {
                    return images.scaleSelectorRelativeHovered;
                }
                else {
                    return images.scaleSelectorRelative;
                }
            case TerceraChartCashItemSeriesDataType.Log:
                if (this.isScaleMenuOpen) {
                    return images.scaleSelectorLogSelected;
                }
                else if (this.isHoverScaleButton) {
                    return images.scaleSelectorLogHovered;
                }
                else {
                    return images.scaleSelectorLog;
                }
        }
    }
    hoverScaleButton(e) {
        const contains = this.ScaleButtonRect.Contains(e.Location.X, e.Location.Y);
        const oldStateHover = this.isHoverScaleButton;
        this.isHoverScaleButton = contains;
        if (oldStateHover !== this.isHoverScaleButton) {
            e.NeedRedraw = true;
        }
    }
    getCashItemSeriesSettings() {
        var _a;
        const cashItemSeriesSettings = ((_a = this.window) === null || _a === void 0 ? void 0 : _a.TerceraChartOverlayWindow) && this.chart.activeOverlay
            ? this.chart.activeOverlay.cashItemSeriesSettings
            : this.chart.cashItemSeriesSettings;
        return cashItemSeriesSettings;
    }
    scaleButtonMouseClick(e) {
        if (this.isScaleMenuOpen) {
            this.chart.TerceraChartActionProcessor.ProcessTerceraChartAction(TerceraChartAction.Create(TerceraChartActionEnum.CloseContextMenu));
            return true;
        }
        const window = this.window;
        const items = [
            this.chart.TerceraChartActionProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.Absolute, window)),
            this.chart.TerceraChartActionProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.Relative, window)),
            this.chart.TerceraChartActionProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.Log, window))
        ];
        // size menu
        const DEFAULT_ROW_HEIGHT = 32;
        const WIDTH = 200;
        const menuPaddding = 4;
        const menuHeight = items.length * DEFAULT_ROW_HEIGHT + menuPaddding * 2;
        // coordinates of the click inside the button
        const XClickInsideButton = e.Location.X - this.ScaleButtonRect.X;
        const YClickInsideButton = e.Location.Y - this.ScaleButtonRect.Y;
        // new coordinate for menu
        const offset = 1;
        let newCoordX = e.ClientLocation.X - XClickInsideButton - WIDTH - offset;
        if (this.WindowType === TerceraChartPriceScaleRendererWindowType.Overlay) {
            newCoordX = e.ClientLocation.X - XClickInsideButton + this.ScaleButtonRect.Width + offset;
        }
        const newCoordY = e.ClientLocation.Y - YClickInsideButton - menuHeight + this.ScaleButtonRect.Height;
        const point = new Point(newCoordX, newCoordY);
        const details = {
            Point: point,
            Items: items,
            closeCallback: () => {
                this.isScaleMenuOpen = false;
                this.chart.IsDirty(this.assignLayer);
            }
        };
        this.chart.TerceraChartActionProcessor.ProcessTerceraChartAction(TerceraChartAction.Create(TerceraChartActionEnum.ScaleSelector, details));
        this.isScaleMenuOpen = true;
        return true;
    }
    // #endregion
    drawAutoSwitcher(gr, isAutoScale, RX, RY, RW, RH) {
        if (!this.settings.AutoScaleSwitcherVisible) {
            return;
        }
        gr.save();
        gr.beginPath();
        gr.rect(RX, RY, RW, RH);
        gr.clip();
        // For this renderer, its own Rectangle is already set. No need to take window.ClientRectangle
        // This is for drawing indicator panels and overlays in the upper part of the window, details (alexb, Zhenya)
        const AutoScaleRect = new Rectangle(RX, RY, RW, this.GetScaleFontHeight() + 32);
        this.AutoScaleRect = AutoScaleRect;
        const ASRX = AutoScaleRect.X;
        const ASRY = AutoScaleRect.Y;
        const ASRW = AutoScaleRect.Width;
        const ASRH = AutoScaleRect.Height;
        // Draw the autoscale switcher buttons
        gr.fillStyle = this.settings.scaleBackBrush.Color;
        gr.fillRect(ASRX, ASRY, ASRW, ASRH); // Fill the background of the autoscale switcher area with the scaleBackBrush color
        // Determine the text to display on the button based on the current autoscale state
        const buttonText = isAutoScale ? this.autoText : this.manualText;
        // Draw the button text using the ScaleFont and scaleTextBrush
        gr.DrawString(buttonText, this.settings.ScaleFont, this.settings.scaleTextBrush, ASRX + ASRW / 2, ASRY + ASRH / 4, 'center', 'middle');
        const sliderSize = 16;
        const trackWidth = 24;
        const trackHeight = 12;
        const gap = 4;
        const leftPadding = 8;
        const btnX = ASRX + leftPadding;
        const btnY = ASRY + this.settings.ScaleFont.getCorrectHeight() + gap;
        const tractRect = new Rectangle(btnX + sliderSize / 2, btnY + (sliderSize - trackHeight) / 2, trackWidth, trackHeight);
        const trackPen = new Pen(ThemeManager.colors.chartSwitcherTrackBackground);
        gr.RoundRectangle(tractRect, 6, null, trackPen);
        const sliderBrush = new SolidBrush(this.AutoScaleSwitcherHovered
            ? ThemeManager.colors.chartSwitcherSliderBackgroundHovered
            : ThemeManager.colors.chartSwitcherSliderBackground);
        const sliderRect = new Rectangle(isAutoScale ? btnX : btnX + trackWidth, btnY, sliderSize, sliderSize);
        gr.FillEllipse(sliderBrush, sliderRect.X, sliderRect.Y, sliderRect.Width, sliderRect.Height);
        if (sliderRect.X < tractRect.X) {
            this.AutoScaleSwitcherRectangle = new Rectangle(sliderRect.X, sliderRect.Y, tractRect.Width + sliderRect.Width / 2, sliderRect.Height);
        }
        else {
            this.AutoScaleSwitcherRectangle = new Rectangle(tractRect.X, sliderRect.Y, sliderRect.X + sliderRect.Width, sliderRect.Height);
        }
        // Reset clip.
        gr.restore();
    }
}
// Scale button
TerceraChartPriceScaleRenderer.SCALE_BUTTON_SIZE = 32;
TerceraChartPriceScaleRenderer.LEFT_PADDING = 4;
TerceraChartPriceScaleRenderer.MIN_WIDTH = 56;
export class TerceraChartPriceScaleRendererSettings extends TerceraChartNumberScaleRendererSettings {
    get RelativeCalculationType() {
        return this.relativeCalculationType;
    }
    set RelativeCalculationType(value) {
        this.relativeCalculationType = value;
    }
    constructor() {
        super();
        this.HighlightMarkingsStep = 100;
        this.HighlightMarkings = false;
        this.relativeCalculationType = RelativeCalculationType.BeginOfTheScreen;
        this.UseCustomYMarkings = false;
        this.UseCustomXMarkings = false;
        this.CustomYMarkingValue = 1;
        this.CustomXMarkingValue = 10;
        this.AutoScaleSwitcherVisible = true;
        this.gridPriceHLPen = new Pen('', PenStyle.IsoDotChart, 1);
        this.PercentView = false;
    }
    ThemeChanged() {
        super.ThemeChanged();
        this.gridPriceHLPen.Color = ThemeManager.colors.chartGridColor;
    }
    Properties() {
        const properties = [];
        // Grid
        let SeparatorGroup = '#-4#' + Resources.getResource('property.SeparatorGroup.Grid');
        let colorStyleWidth = new ColorStyleWidth(this.ScaleGridColor, this.ScaleGridStyle, this.ScaleGridWidth);
        colorStyleWidth.CheckBoxVisible = true;
        colorStyleWidth.Checked = this.ScaleGridVisibility;
        // colorStyleWidth.DisableWidth = true;
        let prop = new DynProperty('GridPrice', colorStyleWidth, DynProperty.COLOR_STYLE_WIDTH, DynProperty.VISUAL_GROUP);
        prop.separatorGroup = SeparatorGroup;
        prop.sortIndex = 0;
        properties.push(prop);
        // Appearance
        SeparatorGroup = '#3#' + Resources.getResource('property.SeparatorGroup.Appearance');
        prop = new DynProperty('PriceScaleTextColor', this.ScaleTextColor, DynProperty.COLOR, DynProperty.PRICE_SCALE_GROUP);
        prop.sortIndex = 1;
        prop.separatorGroup = SeparatorGroup;
        properties.push(prop);
        // Scale markings
        SeparatorGroup = '#2#' + Resources.getResource('property.SeparatorGroup.ScaleMarkings');
        prop = new DynProperty('UseCustomYMarkings', this.UseCustomYMarkings, DynProperty.BOOLEAN, DynProperty.PRICE_SCALE_GROUP);
        prop.sortIndex = 0;
        prop.separatorGroup = SeparatorGroup;
        prop.assignedProperty = ['CustomYMarkingValue'];
        properties.push(prop);
        prop = new DynProperty('CustomYMarkingValue', this.CustomYMarkingValue, DynProperty.DOUBLE, DynProperty.PRICE_SCALE_GROUP);
        prop.sortIndex = 1;
        prop.enabled = this.UseCustomYMarkings;
        prop.minimalValue = 0;
        prop.decimalPlaces = 1;
        prop.increment = 1;
        prop.separatorGroup = SeparatorGroup;
        properties.push(prop);
        prop = new DynProperty('HighlightMarkingsStep', new BoolNumeric(this.HighlightMarkings, this.HighlightMarkingsStep), DynProperty.BOOL_NUMERIC, DynProperty.PRICE_SCALE_GROUP);
        prop.sortIndex = 2;
        prop.decimalPlaces = 0;
        prop.increment = 1;
        prop.minimalValue = 0;
        prop.assignedProperty = ['GridPriceHighLight'];
        prop.separatorGroup = SeparatorGroup;
        properties.push(prop);
        colorStyleWidth = new ColorStyleWidth(this.gridPriceHLPen.Color, this.gridPriceHLPen.DashStyle, this.gridPriceHLPen.Width);
        colorStyleWidth.CheckBoxVisible = false;
        // colorStyleWidth.DisableWidth = true;
        prop = new DynProperty('GridPriceHighLight', colorStyleWidth, DynProperty.COLOR_STYLE_WIDTH, DynProperty.PRICE_SCALE_GROUP);
        prop.separatorGroup = SeparatorGroup;
        prop.enabled = this.HighlightMarkings;
        prop.sortIndex = 3;
        properties.push(prop);
        return properties;
    }
    callBack(properties) {
        let dp = DynProperty.getPropertyByName(properties, 'relativeCalcType');
        if (DynProperty.isCorrect(dp))
            this.RelativeCalculationType = dp.value;
        dp = DynProperty.getPropertyByName(properties, 'UseCustomYMarkings');
        if (DynProperty.isCorrect(dp))
            this.UseCustomYMarkings = dp.value;
        dp = DynProperty.getPropertyByName(properties, 'UseCustomXMarkings');
        if (DynProperty.isCorrect(dp))
            this.UseCustomXMarkings = dp.value;
        dp = DynProperty.getPropertyByName(properties, 'CustomYMarkingValue');
        if (DynProperty.isCorrect(dp))
            this.CustomYMarkingValue = dp.value;
        dp = DynProperty.getPropertyByName(properties, 'CustomXMarkingValue');
        if (DynProperty.isCorrect(dp))
            this.CustomXMarkingValue = dp.value;
        dp = DynProperty.getPropertyByName(properties, 'HighlightMarkingsStep');
        if (DynProperty.isCorrect(dp)) {
            const val = dp.value;
            this.HighlightMarkings = val.Checked;
            this.HighlightMarkingsStep = val.Value;
        }
        dp = DynProperty.getPropertyByName(properties, 'GridPriceHighLight');
        if (DynProperty.isCorrect(dp)) {
            const csw = dp.value;
            this.gridPriceHLPen.setNewProperties(csw);
        }
        dp = DynProperty.getPropertyByName(properties, 'GridPrice');
        if (DynProperty.isCorrect(dp)) {
            const csw = dp.value;
            this.ScaleGridColor = csw.Color;
            this.ScaleGridStyle = csw.Style;
            this.ScaleGridWidth = csw.Width;
            this.ScaleGridVisibility = csw.Checked;
        }
        dp = DynProperty.getPropertyByName(properties, 'property.Backgr');
        if (DynProperty.isCorrect(dp))
            this.ScaleBackColor = dp.value;
        dp = DynProperty.getPropertyByName(properties, 'PriceScaleTextColor');
        if (DynProperty.isCorrect(dp))
            this.ScaleTextColor = dp.value;
    }
}
export var TerceraChartPriceScaleRendererMouseState;
(function (TerceraChartPriceScaleRendererMouseState) {
    TerceraChartPriceScaleRendererMouseState[TerceraChartPriceScaleRendererMouseState["None"] = 0] = "None";
    TerceraChartPriceScaleRendererMouseState[TerceraChartPriceScaleRendererMouseState["Moving"] = 1] = "Moving";
    TerceraChartPriceScaleRendererMouseState[TerceraChartPriceScaleRendererMouseState["ZoomIn"] = 2] = "ZoomIn";
    TerceraChartPriceScaleRendererMouseState[TerceraChartPriceScaleRendererMouseState["ZoomOut"] = 3] = "ZoomOut";
})(TerceraChartPriceScaleRendererMouseState || (TerceraChartPriceScaleRendererMouseState = {}));
export var TerceraChartPriceScaleRendererWindowType;
(function (TerceraChartPriceScaleRendererWindowType) {
    TerceraChartPriceScaleRendererWindowType[TerceraChartPriceScaleRendererWindowType["Main"] = 0] = "Main";
    TerceraChartPriceScaleRendererWindowType[TerceraChartPriceScaleRendererWindowType["Overlay"] = 1] = "Overlay";
    TerceraChartPriceScaleRendererWindowType[TerceraChartPriceScaleRendererWindowType["Indicator"] = 2] = "Indicator";
    TerceraChartPriceScaleRendererWindowType[TerceraChartPriceScaleRendererWindowType["IndicatorOverlay"] = 3] = "IndicatorOverlay";
})(TerceraChartPriceScaleRendererWindowType || (TerceraChartPriceScaleRendererWindowType = {}));
export var RelativeCalculationType;
(function (RelativeCalculationType) {
    RelativeCalculationType[RelativeCalculationType["BeginOfTheScreen"] = 0] = "BeginOfTheScreen";
    RelativeCalculationType[RelativeCalculationType["BeginOfTheData"] = 1] = "BeginOfTheData";
    RelativeCalculationType[RelativeCalculationType["BeginOfTheDay"] = 2] = "BeginOfTheDay";
    RelativeCalculationType[RelativeCalculationType["CustomValue"] = 3] = "CustomValue";
    RelativeCalculationType[RelativeCalculationType["CustomDate"] = 4] = "CustomDate";
})(RelativeCalculationType || (RelativeCalculationType = {}));
export var TerceraChartPriceScaleRendererAligment;
(function (TerceraChartPriceScaleRendererAligment) {
    TerceraChartPriceScaleRendererAligment[TerceraChartPriceScaleRendererAligment["Right"] = 0] = "Right";
    TerceraChartPriceScaleRendererAligment[TerceraChartPriceScaleRendererAligment["Left"] = 1] = "Left";
})(TerceraChartPriceScaleRendererAligment || (TerceraChartPriceScaleRendererAligment = {}));
